/**
 * Button to initiate voice chat mode
 *
 * Shows a prominent button with microphone icon to start real-time voice conversation
 */

import React from 'react';
import { Mic } from 'lucide-react';

interface VoiceChatButtonProps {
  onClick: () => void;
  onIntent?: () => void;
  disabled?: boolean;
  isDarkMode?: boolean;
}

export const VoiceChatButton: React.FC<VoiceChatButtonProps> = ({
  onClick,
  onIntent,
  disabled = false,
  isDarkMode = false,
}) => {
  return (
    <button
      onClick={onClick}
      onPointerEnter={onIntent}
      onFocus={onIntent}
      onTouchStart={onIntent}
      disabled={disabled}
      className={`
        group relative flex items-center justify-center
        w-12 h-12 sm:w-14 sm:h-14 rounded-full flex-shrink-0
        transition-all duration-300 ease-out
        ${
          disabled
            ? 'bg-gray-300 cursor-not-allowed'
            : isDarkMode
            ? 'bg-gradient-to-br from-[#ed7203] to-[#e0a240] hover:from-[#c75f02] hover:to-[#ed7203] shadow-lg hover:shadow-orange-500/30'
            : 'bg-gradient-to-br from-[#ed7203] to-[#e0a240] hover:from-[#c75f02] hover:to-[#ed7203] shadow-lg hover:shadow-orange-400/30'
        }
        ${!disabled && 'hover:scale-105 active:scale-95'}
      `}
      title="Voice Call starten"
      aria-label="Start voice conversation"
    >
      {/* Pulse animation ring */}
      {!disabled && (
        <span className="absolute inset-0 rounded-full animate-ping opacity-20 bg-orange-400" />
      )}

      {/* Icon */}
      <Mic
        className={`
          w-6 h-6 text-white
          transition-transform duration-200
          ${!disabled && 'group-hover:scale-110'}
        `}
      />

      {/* Tooltip */}
      <span
        className={`
          absolute -bottom-10 left-1/2 -translate-x-1/2
          px-2 py-1 text-xs font-medium rounded
          whitespace-nowrap opacity-0 group-hover:opacity-100
          transition-opacity duration-200
          pointer-events-none
          ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-900 text-white'}
        `}
      >
        Voice Chat
      </span>
    </button>
  );
};

export default VoiceChatButton;
